import { Component, DestroyRef, inject, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IContact, ITag } from '../../../../../types/contactModule';
import { ContactsService } from '../../../../../services/contacts/contacts.service';
import { MessageService } from '../../../../../services/message/message.service';
import { GroupAudienceService, GroupRef, Reach } from '../../../../../services/segments/group-audience.service';
import {
  GroupContactsTableComponent,
  TMemberAction,
} from '../../../../../components/tables/group-contacts-table/group-contacts-table.component';
import { AssignContactsToGroupComponent } from '../../../../../components/modals/assign-contacts-to-group/assign-contacts-to-group.component';
import { EditTagModalComponent } from '../../../../../components/forms/edit-tag-modal/edit-tag-modal.component';
import { PromptCardComponent } from '../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { BackButtonComponent } from '../../../../../components/ui/back-button/back-button.component';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { makeArrayUnique } from '../../../../../../utils/utils';
import { formatCount, formatWhen } from '../../../../../../utils/sms';
import { contactName } from '../../../../../components/tables/contact-table/contact-row';

@Component({
  selector: 'app-tag-details',
  standalone: true,
  imports: [
    GroupContactsTableComponent,
    AssignContactsToGroupComponent,
    EditTagModalComponent,
    PromptCardComponent,
    BackButtonComponent,
    HasPermissionDirective,
  ],
  templateUrl: './tag-details.component.html',
  styleUrls: ['../../../../../components/tables/group-table/group-details.css', './tag-details.component.css'],
})
export class TagDetailsComponent implements OnChanges, OnInit {
  private location = inject(Location);
  private destroyRef = inject(DestroyRef);
  private audience = inject(GroupAudienceService);
  private messageService = inject(MessageService);
  contactService = inject(ContactsService);
  router = inject(Router);

  @ViewChild(GroupContactsTableComponent) membersTable?: GroupContactsTableComponent;

  /** The tag's uuid, from the route. */
  @Input() id!: string;

  readonly formatCount = formatCount;

  tag: ITag | null = null;
  tagId: string | number = '';
  loading = true;
  loadError = '';
  private loadSub?: Subscription;
  private countSub?: Subscription;

  contactsCount: number | null = null;
  /** The member count did not load. */
  countError = false;
  reach: Reach | null = null;
  selectedContacts: IContact[] = [];

  showAddAnotherContact: boolean = false;
  assigningContactsToTags: boolean = false;
  showEditModal = false;

  showRemoveContactsPrompt: boolean = false;
  removing = false;
  contactsToRemove: IContact[] = [];
  /** The prompt came from one row's Remove, not the selection bar. */
  private removingOneRow = false;

  get ref(): GroupRef | null {
    return this.tag?.uuid ? { kind: 'tag', uuid: this.tag.uuid, id: this.tag.id ?? null } : null;
  }

  get name(): string {
    return (this.tag?.name ?? '').toString().trim() || 'Untitled tag';
  }

  get description(): string {
    return (this.tag?.description ?? '').toString().trim();
  }

  get created(): string {
    return formatWhen(this.tag?.createdAt);
  }

  get updated(): string {
    return formatWhen(this.tag?.modifiedAt || this.tag?.createdAt);
  }

  get removeTitle(): string {
    const count = this.contactsToRemove.length;
    if (count === 1) return `Remove ${contactName(this.contactsToRemove[0]) || 'this contact'} from ${this.name}?`;
    return `Remove ${count} contacts from ${this.name}?`;
  }

  get removeButton(): string {
    return this.contactsToRemove.length === 1 ? 'Remove contact' : `Remove ${this.contactsToRemove.length} contacts`;
  }

  // ── Loading ──
  loadTag() {
    if (!this.id) return;
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.loadError = '';
    this.loadSub = this.audience
      .getTag(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tag) => {
          this.tag = tag;
          this.tagId = tag?.uuid ?? '';
          this.loading = false;
          this.handleFetchTagContactsCount();
        },
        error: () => {
          this.loading = false;
          this.loadError = 'This tag did not load. It may have been deleted, or the connection dropped.';
        },
      });
  }

  /** Member count, then how many members have no phone number. */
  handleFetchTagContactsCount() {
    const ref = this.ref;
    if (!ref) return;
    this.countError = false;
    // A newer count replaces one still in flight, so a slow, older reply never lands last.
    this.countSub?.unsubscribe();
    this.countSub = this.audience
      .memberCount(ref)
      .pipe(
        switchMap((count) => {
          this.contactsCount = count;
          return this.audience.reach(ref, count);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (reach) => (this.reach = reach),
        error: () => {
          this.contactsCount = null;
          this.countError = true;
        },
      });
  }

  private membersChanged() {
    this.audience.invalidate(this.ref);
    this.handleFetchTagContactsCount();
  }

  onMembersChanged() {
    this.membersChanged();
  }

  // ── Header actions ──
  goBack() {
    this.location.back(); // Navigates back in the platform's history
  }

  /** Opens the composer with this tag already picked. */
  messageGroup() {
    if (!this.tag) return;
    this.messageService.updateSelectedTags([this.tag]);
    this.router.navigate(['/organization/messages/create']);
  }

  openEdit() {
    this.showEditModal = true;
  }

  closeEdit() {
    this.showEditModal = false;
    this.loadTag();
  }

  openShowAddAnotherContact() {
    this.showAddAnotherContact = true;
  }
  closeShowAddAnotherContact() {
    this.showAddAnotherContact = false;
  }

  handleAssignContactsToTag(selectedContacts: IContact[]) {
    if (!selectedContacts?.length || !this.tag?.uuid) return;
    this.assigningContactsToTags = true;
    this.contactService
      .assignMultipleContactsToTags(
        selectedContacts.map((el) => el.uuid as string),
        [this.tag.uuid as string]
      )
      .subscribe({
        next: () => {
          this.assigningContactsToTags = false;
          this.closeShowAddAnotherContact();
          this.membersChanged();
          this.membersTable?.refresh();
        },
        error: () => {
          this.assigningContactsToTags = false;
          this.contactService.showError('The contacts were not tagged. Try again.');
        },
      });
  }

  // ── Members ──
  updateSelectedContacts(contacts: IContact[]) {
    this.selectedContacts = contacts;
  }

  resetSelectedContacts() {
    this.selectedContacts = [];
    this.membersTable?.unselectAll();
  }

  get getSelectedContacts() {
    return makeArrayUnique(this.selectedContacts);
  }

  handleAction(action: TMemberAction | string) {
    if (this.selectedContacts.length === 0) return;
    switch (action) {
      case 'send-sms':
        this.sendSMStoSelectedContacts();
        break;
      case 'remove':
        this.openRemoveContactsPrompt();
        break;
    }
  }

  sendSMStoSelectedContacts() {
    const params = { items: JSON.stringify(this.selectedContacts) };
    this.router.navigate(['/organization/messages/create'], {
      queryParams: params,
    });
    this.resetSelectedContacts();
  }

  openRemoveContactsPrompt(contacts?: IContact[]) {
    this.removingOneRow = !!contacts?.length;
    this.contactsToRemove = contacts?.length ? contacts : this.getSelectedContacts;
    if (this.contactsToRemove.length) this.showRemoveContactsPrompt = true;
  }

  removeOne(contact: IContact) {
    this.openRemoveContactsPrompt([contact]);
  }

  closeRemoveContactsPrompt() {
    if (this.removing) return;
    this.showRemoveContactsPrompt = false;
  }

  // api functionality to remove the contacts from the Tag
  removeSelectedContactsFromTag() {
    const uuid = this.tag?.uuid;
    const contacts = this.contactsToRemove;
    if (!uuid || !contacts.length || this.removing) return;
    this.removing = true;
    this.contactService
      .removeTagContacts(
        uuid,
        contacts.map((el) => el.uuid as string)
      )
      .subscribe({
        next: () => {
          this.removing = false;
          this.showRemoveContactsPrompt = false;
          // A row's Remove leaves the other ticked members ticked.
          if (this.removingOneRow) {
            this.membersTable?.deselect(contacts);
          } else {
            this.resetSelectedContacts();
          }
          this.membersChanged();
          this.membersTable?.load();
          this.contactService.showSuccess(
            contacts.length === 1 ? 'Tag removed from the contact.' : `Tag removed from ${contacts.length} contacts.`
          );
        },
        error: () => {
          this.removing = false;
          this.showRemoveContactsPrompt = false;
          this.contactService.showError('The tag was not removed from those contacts. Try again.');
        },
      });
  }

  ngOnChanges() {
    // The route reuses this component when moving between tags.
    this.tag = null;
    this.countSub?.unsubscribe();
    this.contactsCount = null;
    this.reach = null;
    this.selectedContacts = [];
    this.loadTag();
  }

  ngOnInit() {
    if (!this.loadSub) this.loadTag();
  }
}
